import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslate } from '@/translations';
import { Alert, Form, Input, Modal, Select, Spin, Switch } from 'antd';
import {
  Country,
  Select as SelectType,
  ServiceProvider,
  ServiceUnit,
  ServiceUnitType,
} from '@/types';

type UnitPopupProps = {
  data?: ServiceUnit;
  countries: Country[];
  serviceUnitTypes: ServiceUnitType[];
  serviceProviders: ServiceProvider[];
  customers: SelectType<number>[];
  onSubmit(value: ServiceUnit): Promise<string | undefined>;
  onCancel(): void;
};

export const UnitPopup: FC<UnitPopupProps> = ({
  data,
  countries,
  serviceUnitTypes,
  serviceProviders,
  customers,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslate();
  const [form] = Form.useForm<ServiceUnit>();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const isAdd = data && !data.id;

  useEffect(() => {
    setErrorMessage(undefined);
    form.resetFields();
    if (data) form.setFieldsValue(data);
  }, [data, form]);

  const handleSubmit = useCallback(
    async (value: ServiceUnit) => {
      setErrorMessage(undefined);
      setLoading(true);
      const error = await onSubmit(value);
      setErrorMessage(error);

      setLoading(false);
      if (!error) onCancel();
    },
    [onCancel, onSubmit],
  );

  return (
    <Modal
      open={!!data}
      onCancel={onCancel}
      title={`${t(isAdd ? 'create' : 'edit')} Unit`}
      maskClosable
      closable={false}
      okText={t('save')}
      cancelText={t('cancel')}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={680}
      className="mw-100"
      forceRender
    >
      <Spin spinning={loading}>
        <Form
          id="unit-form"
          form={form}
          onFinish={handleSubmit}
          size="large"
          labelCol={{ span: 6 }}
          autoComplete="off"
        >
          <Form.Item<ServiceUnit> name="id" hidden>
            <Input />
          </Form.Item>

          {isAdd ? (
            <Form.Item<ServiceUnit>
              label={t('customer')}
              name="customerId"
              required
              rules={[{ required: true }]}
            >
              <Select showSearch options={customers} optionFilterProp="label" />
            </Form.Item>
          ) : null}

          <Form.Item<ServiceUnit>
            label="Unit Ref"
            name="ref"
            required
            rules={[{ required: true }]}
          >
            <Input maxLength={50} />
          </Form.Item>

          <Form.Item<ServiceUnit>
            label="Unit Name"
            name="name"
            required
            rules={[{ required: true }]}
          >
            <Input maxLength={50} />
          </Form.Item>

          <Form.Item<ServiceUnit> label={t('country')} name="countryId">
            <Select
              showSearch
              options={countries.map((o) => ({
                value: o.id,
                label: `${o.name} (${o.iso})`,
              }))}
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item<ServiceUnit> label="Unit Type" name="serviceUnitTypeId">
            <Select
              showSearch
              options={serviceUnitTypes.map((o) => ({
                value: o.id,
                label: o.name,
              }))}
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item<ServiceUnit> label="Provider" name="serviceProviderId">
            <Select
              showSearch
              options={serviceProviders.map((o) => ({
                value: o.id,
                label: o.name,
              }))}
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item<ServiceUnit>
            name="isActive"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          {errorMessage ? (
            <Alert message={errorMessage} type="error" className="mb-4" />
          ) : null}
        </Form>
      </Spin>
    </Modal>
  );
};
